/**
 * Health fetch — maps GET /api/health to one of the four view states the badge renders.
 * 200            -> healthy
 * 503 (+ body)   -> degraded, with the dependency reason from the body
 * fetch error /  -> cannot_reach (no HTTP response at all — distinct from a 503)
 *   client timeout
 */
export type HealthView =
  | { view: 'healthy'; message: string }
  | { view: 'degraded'; message: string }
  | { view: 'cannot_reach'; message: string };

const CLIENT_TIMEOUT_MS = 5000;

interface HealthResponseBody {
  status?: 'ok' | 'error';
  error?: Record<string, { status?: string; message?: string }>;
}

export async function fetchHealth(signal?: AbortSignal): Promise<HealthView> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);
  if (signal) {
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const res = await fetch('/api/health', {
      headers: { accept: 'application/json' },
      signal: controller.signal,
    });

    if (res.ok) {
      return { view: 'healthy', message: 'All systems operational.' };
    }

    // A 503 still carries a body naming the failing dependency.
    const body = (await res.json().catch(() => ({}))) as HealthResponseBody;
    const reason = firstErrorMessage(body) ?? 'A dependency is unavailable.';
    return { view: 'degraded', message: `Server up, database down — ${reason}` };
  } catch {
    // No response: server unreachable, network down, or the client timeout fired.
    return {
      view: 'cannot_reach',
      message: 'Cannot reach the server. Check that the backend is running.',
    };
  } finally {
    clearTimeout(timer);
  }
}

function firstErrorMessage(body: HealthResponseBody): string | undefined {
  if (!body.error) return undefined;
  for (const dep of Object.values(body.error)) {
    if (dep?.message) return dep.message;
  }
  return undefined;
}
